import { cn } from '@/lib/utils'

export interface StackedSegment {
  key: string
  label: string
  value: number
  /** A fill class: `bg-chart-1`, `bg-chart-1/55` … */
  fill: string
}

/** One bar split into shares, with a 2px gap between the parts. Parts with
 *  no value are left out; the whole reads out as "Top 10 3, 11–50 5 …". */
export function StackedBar({ segments, height = 8, className, label }: { segments: StackedSegment[]; height?: number; className?: string; label?: string }) {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0)
  const shown = segments.filter((s) => s.value > 0)
  const name = label ?? segments.map((s) => `${s.label} ${s.value}`).join(', ')
  return (
    <div role="img" aria-label={name} style={{ height }} className={cn('flex w-full min-w-0 gap-[2px] overflow-hidden rounded-full', total === 0 && 'bg-muted', className)}>
      {total > 0 &&
        shown.map((s) => (
          <span key={s.key} title={`${s.label} ${s.value}`} className={cn('h-full min-w-[2px]', s.fill)} style={{ flexGrow: s.value, flexBasis: 0 }} />
        ))}
    </div>
  )
}

/** A rank-distribution row's counts. */
export interface RankBuckets {
  top_10: number
  top_50: number
  ranked_50_plus: number
  unranked: number
}

/** The rank distribution in one hue, darkest for the best bucket (plan 1-3):
 *  Top 10 · 11–50 · 51+ · 순위 밖. */
export function rankSegments(b: RankBuckets): StackedSegment[] {
  return [
    { key: 'top10', label: 'Top 10', value: b.top_10, fill: 'bg-chart-1' },
    { key: 'top50', label: '11–50', value: b.top_50, fill: 'bg-chart-1/55' },
    { key: 'rest', label: '51+', value: b.ranked_50_plus, fill: 'bg-chart-1/25' },
    { key: 'unranked', label: '순위 밖', value: b.unranked, fill: 'bg-muted-foreground/20' },
  ]
}
